import React, { useRef } from 'react';
import type { Chart as ChartJS } from 'chart.js';
import { Doughnut } from 'react-chartjs-2';
import { chartElementAtEvent } from '../chartInteraction';
import KpiChartPanel from './KpiChartPanel';

function RateRingGreen({ percent }: { percent: number }) {
  const p = Math.min(100, Math.max(0, Number.isFinite(percent) ? percent : 0));
  const size = 132;
  const stroke = 11;
  const r = (size - stroke) / 2;
  const cx = size / 2;
  const cy = size / 2;
  const circumference = 2 * Math.PI * r;
  const dash = (p / 100) * circumference;
  const gap = Math.max(0, circumference - dash);

  return (
    <svg width={size} height={size} viewBox={`0 0 ${size} ${size}`} className="shrink-0 drop-shadow-sm" aria-hidden>
      <circle cx={cx} cy={cy} r={r} fill="none" stroke="#e2e8f0" strokeWidth={stroke} />
      <circle
        cx={cx}
        cy={cy}
        r={r}
        fill="none"
        stroke="rgb(34, 197, 94)"
        strokeWidth={stroke}
        strokeLinecap="round"
        strokeDasharray={`${dash} ${gap}`}
        transform={`rotate(-90 ${cx} ${cy})`}
        className="transition-[stroke-dasharray] duration-500 ease-out"
      />
    </svg>
  );
}

function formatRateTwoDecimals(value: string): string {
  const n = parseFloat(value);
  if (!Number.isFinite(n)) return '0.00';
  return n.toFixed(2);
}

/** e.g. 1 "Ongoing SLA", 0/2+ "Ongoing SLAs" */
function formatSlaCountLine(count: number, singularPhrase: string): string {
  const n = count.toLocaleString();
  if (count === 1) return `${n} ${singularPhrase}`;
  if (singularPhrase.endsWith(' SLA')) {
    return `${n} ${singularPhrase.slice(0, -4)} SLAs`;
  }
  return `${n} ${singularPhrase}s`;
}

export interface KpiIncidentCommComplianceDonutsProps {
  ongoingCommRatePct: string;
  ongoingCommTicketCount?: number;
  compliantCount: number;
  nonCompliantCount: number;
  /**
   * Legacy: entire left panel opens one drill. Prefer {@link ongoingCompliantCount} +
   * {@link ongoingNonCompliantCount} + slice callbacks + {@link onOngoingCenterDrill} for parity with resolution SLA.
   */
  onOngoingDrill?: () => void;
  /** Met vs breached slice drills (left panel), same UX as resolution donut. */
  ongoingCompliantCount?: number;
  ongoingNonCompliantCount?: number;
  onOngoingCompliantSliceDrill?: () => void;
  onOngoingNonCompliantSliceDrill?: () => void;
  /** Center of ongoing panel — all tickets in ongoing SLA pool (matches chart scope). */
  onOngoingCenterDrill?: () => void;
  /** Ongoing SLA instances met (matches KPI donut “Compliant” slice). */
  onCompliantSliceDrill?: () => void;
  /** Ongoing SLA instances breached (matches “Non-Compliant” slice). */
  onNonCompliantSliceDrill?: () => void;
  /** Center of resolution panel — all tickets in resolution SLA pool. */
  onResolutionCenterDrill?: () => void;
  /** Left panel header (default: incidents wording). */
  ongoingHeaderText?: string;
  /** Right panel header above the donut. */
  complianceHeaderText?: string;
  /** Label under the ongoing rate ring (count prefix is formatted automatically). */
  ongoingFooterLabel?: string;
  /** Label under the compliance ring (count prefix is formatted automatically). */
  complianceFooterLabel?: string;
  /** Extra classes on the outer panel (e.g. max-height in a constrained grid). */
  panelClassName?: string;
  /** When true, only the compliance donut column is shown (no left rate ring). */
  hideOngoingPanel?: boolean;
}

const KpiIncidentCommComplianceDonuts: React.FC<KpiIncidentCommComplianceDonutsProps> = ({
  ongoingCommRatePct,
  ongoingCommTicketCount,
  compliantCount,
  nonCompliantCount,
  onOngoingDrill,
  ongoingCompliantCount,
  ongoingNonCompliantCount,
  onOngoingCompliantSliceDrill,
  onOngoingNonCompliantSliceDrill,
  onOngoingCenterDrill,
  onCompliantSliceDrill,
  onNonCompliantSliceDrill,
  onResolutionCenterDrill,
  ongoingHeaderText = 'Incidents Ongoing Comm Rate',
  complianceHeaderText = 'Compliant / Non-Compliant',
  ongoingFooterLabel = 'Ongoing SLA',
  complianceFooterLabel = 'Resolution SLA',
  panelClassName = '',
  hideOngoingPanel = false,
}) => {
  const ongoingDoughnutRef = useRef<ChartJS<'doughnut'>>(null);
  const resolutionDoughnutRef = useRef<ChartJS<'doughnut'>>(null);
  const pct = parseFloat(ongoingCommRatePct);
  const display = formatRateTwoDecimals(ongoingCommRatePct);
  const total = Math.max(0, compliantCount + nonCompliantCount);
  const pctCompliant = total > 0 ? (compliantCount / total) * 100 : 0;
  const complianceDisplay = formatRateTwoDecimals(String(pctCompliant));

  const ongoingMet = ongoingCompliantCount ?? 0;
  const ongoingBreached = ongoingNonCompliantCount ?? 0;
  const ongoingSliceDrillEnabled = Boolean(
    onOngoingCompliantSliceDrill &&
      onOngoingNonCompliantSliceDrill &&
      (ongoingMet > 0 || ongoingBreached > 0)
  );
  const useSlicedOngoingLayout = Boolean(
    onOngoingCompliantSliceDrill && onOngoingNonCompliantSliceDrill && (ongoingSliceDrillEnabled || onOngoingCenterDrill)
  );

  const resolutionSliceDrillEnabled = Boolean(onCompliantSliceDrill || onNonCompliantSliceDrill);

  const handleOngoingDoughnutClick = (event: React.MouseEvent<HTMLCanvasElement>) => {
    if (!ongoingSliceDrillEnabled || !ongoingDoughnutRef.current) return;
    const els = chartElementAtEvent(ongoingDoughnutRef.current, event);
    const el = els[0];
    if (el == null) return;
    if (el.index === 0 && ongoingMet > 0) onOngoingCompliantSliceDrill?.();
    if (el.index === 1 && ongoingBreached > 0) onOngoingNonCompliantSliceDrill?.();
  };

  const handleResolutionDoughnutClick = (event: React.MouseEvent<HTMLCanvasElement>) => {
    if (!resolutionSliceDrillEnabled || !resolutionDoughnutRef.current) return;
    const els = chartElementAtEvent(resolutionDoughnutRef.current, event);
    const el = els[0];
    if (el == null) return;
    if (el.index === 0 && compliantCount > 0) onCompliantSliceDrill?.();
    if (el.index === 1 && nonCompliantCount > 0) onNonCompliantSliceDrill?.();
  };

  const InnerOngoingHeader = (
    <div className="border-b border-slate-100 bg-gradient-to-r from-slate-50 to-emerald-50/30 px-3 py-2.5 text-center">
      <span className="text-[13px] font-medium leading-snug text-slate-800">{ongoingHeaderText}</span>
    </div>
  );

  const InnerOngoingBodyLegacy = (
    <div className="flex min-h-0 flex-1 flex-col items-center justify-center px-4 py-6">
      <div className="relative inline-flex items-center justify-center">
        <RateRingGreen percent={pct} />
        <div className="pointer-events-none absolute inset-0 flex items-center justify-center">
          <span className="text-lg font-bold tabular-nums text-slate-900">{display}%</span>
        </div>
      </div>
      {ongoingCommTicketCount != null && (
        <p className="mt-2 text-[11px] font-medium text-slate-500">
          {formatSlaCountLine(ongoingCommTicketCount, ongoingFooterLabel)}
        </p>
      )}
    </div>
  );

  const InnerOngoingBodySliced = (
    <div className="flex min-h-0 flex-1 flex-col items-center justify-center px-4 py-6">
      <div className="relative inline-flex h-[132px] w-[132px] shrink-0 items-center justify-center">
        <div className="absolute inset-0 flex items-center justify-center">
          <RateRingGreen percent={pct} />
        </div>
        <div className="pointer-events-none absolute inset-0 z-[1] flex items-center justify-center">
          <span className="text-lg font-bold tabular-nums text-slate-900">{display}%</span>
        </div>
        {onOngoingCenterDrill ? (
          <button
            type="button"
            onClick={onOngoingCenterDrill}
            className="absolute left-1/2 top-1/2 z-[12] h-[52px] w-[52px] -translate-x-1/2 -translate-y-1/2 rounded-full border-0 bg-transparent p-0 text-transparent focus:outline-none focus-visible:ring-2 focus-visible:ring-emerald-500"
            title="View all tickets (ongoing SLA scope)"
            aria-label="View all tickets in ongoing SLA scope"
          >
            ·
          </button>
        ) : null}
        {ongoingSliceDrillEnabled ? (
          <div className="absolute inset-0 z-10 [&_canvas]:opacity-0">
            <Doughnut
              ref={ongoingDoughnutRef}
              onClick={handleOngoingDoughnutClick}
              data={{
                labels: ['Compliant', 'Non-Compliant'],
                datasets: [
                  {
                    data: [ongoingMet, ongoingBreached],
                    backgroundColor: ['#22c55e', '#dc2626'],
                    borderWidth: 0,
                  },
                ],
              }}
              options={{
                responsive: true,
                maintainAspectRatio: false,
                cutout: '78%',
                animation: false,
                onHover: (event, elements) => {
                  const canvas = event.native?.target;
                  if (!(canvas instanceof HTMLElement)) return;
                  const el = elements[0];
                  if (el == null) {
                    canvas.style.cursor = 'default';
                    return;
                  }
                  const count = el.index === 0 ? ongoingMet : ongoingBreached;
                  canvas.style.cursor = count > 0 ? 'pointer' : 'default';
                },
                plugins: {
                  legend: { display: false },
                  tooltip: { enabled: false },
                },
              }}
            />
          </div>
        ) : null}
      </div>
      {ongoingCommTicketCount != null && (
        <p className="mt-2 text-[11px] font-medium text-slate-500">
          {formatSlaCountLine(ongoingCommTicketCount, ongoingFooterLabel)}
        </p>
      )}
    </div>
  );

  const useLegacyOngoingButton = Boolean(onOngoingDrill) && !useSlicedOngoingLayout;

  return (
    <KpiChartPanel
      title=""
      flexFill
      className={['h-full min-h-0', panelClassName].filter(Boolean).join(' ')}
      bodyClassName="p-0"
    >
      <div
        className={`grid min-h-[280px] flex-1 grid-cols-1 divide-y divide-slate-100 lg:min-h-0 ${
          hideOngoingPanel ? '' : 'lg:grid-cols-2 lg:divide-x lg:divide-y-0'
        }`.trim()}
      >
        {!hideOngoingPanel ? (
          <div className="flex min-h-0 min-w-0 flex-1 flex-col bg-gradient-to-b from-white to-slate-50/50">
            {InnerOngoingHeader}
            {useLegacyOngoingButton ? (
              <button
                type="button"
                onClick={onOngoingDrill}
                className="flex min-h-0 min-w-0 flex-1 flex-col text-left transition-colors hover:bg-emerald-50/30 focus:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-emerald-500"
              >
                {InnerOngoingBodyLegacy}
              </button>
            ) : useSlicedOngoingLayout ? (
              <div className="flex min-h-0 min-w-0 flex-1 flex-col">{InnerOngoingBodySliced}</div>
            ) : (
              <div className="flex min-h-0 min-w-0 flex-1 flex-col">{InnerOngoingBodyLegacy}</div>
            )}
          </div>
        ) : null}

        <div className="flex min-h-0 min-w-0 flex-1 flex-col bg-gradient-to-b from-white to-slate-50/50">
          <div className="border-b border-slate-100 bg-gradient-to-r from-slate-50 to-emerald-50/30 px-3 py-2.5 text-center">
            <span className="text-[13px] font-medium leading-snug text-slate-800">{complianceHeaderText}</span>
          </div>
          <div className="flex min-h-0 flex-1 flex-col items-center justify-center px-4 py-6">
            <div className="relative inline-flex h-[132px] w-[132px] shrink-0 items-center justify-center">
              <RateRingGreen percent={pctCompliant} />
              <div className="pointer-events-none absolute inset-0 z-[1] flex items-center justify-center">
                <span className="text-lg font-bold tabular-nums text-slate-900">{complianceDisplay}%</span>
              </div>
              {onResolutionCenterDrill ? (
                <button
                  type="button"
                  onClick={onResolutionCenterDrill}
                  className="absolute left-1/2 top-1/2 z-[12] h-[52px] w-[52px] -translate-x-1/2 -translate-y-1/2 rounded-full border-0 bg-transparent p-0 text-transparent focus:outline-none focus-visible:ring-2 focus-visible:ring-emerald-500"
                  title="View all tickets (resolution SLA scope)"
                  aria-label="View all tickets in resolution SLA scope"
                >
                  ·
                </button>
              ) : null}
              {resolutionSliceDrillEnabled ? (
                <div className="absolute inset-0 z-10 [&_canvas]:opacity-0">
                  <Doughnut
                    ref={resolutionDoughnutRef}
                    onClick={handleResolutionDoughnutClick}
                    data={{
                      labels: ['Compliant', 'Non-Compliant'],
                      datasets: [
                        {
                          data: [compliantCount, nonCompliantCount],
                          backgroundColor: ['#22c55e', '#dc2626'],
                          borderWidth: 0,
                        },
                      ],
                    }}
                    options={{
                      responsive: true,
                      maintainAspectRatio: false,
                      cutout: '78%',
                      animation: false,
                      onHover: (event, elements) => {
                        const canvas = event.native?.target;
                        if (!(canvas instanceof HTMLElement)) return;
                        const el = elements[0];
                        if (el == null) {
                          canvas.style.cursor = 'default';
                          return;
                        }
                        const count = el.index === 0 ? compliantCount : nonCompliantCount;
                        canvas.style.cursor = count > 0 ? 'pointer' : 'default';
                      },
                      plugins: {
                        legend: { display: false },
                        tooltip: { enabled: false },
                      },
                    }}
                  />
                </div>
              ) : null}
            </div>
            <p className="mt-2 text-[11px] font-medium text-slate-500">
              {formatSlaCountLine(total, complianceFooterLabel)}
            </p>
          </div>
        </div>
      </div>
    </KpiChartPanel>
  );
};

export default KpiIncidentCommComplianceDonuts;
